import React from 'react';
import { SourceFilter } from '../../models';
import { PROPERTY_FIELDS } from '../../models/property';
import { Button, Input, Select, SelectOption, Card, CardContent } from '../ui';
import { Plus, Trash2 } from '../ui/icons';

const departmentField = PROPERTY_FIELDS.find((field: any) => field.id === 'department');
const departmentValues = departmentField?.values || {};

interface SourceFiltersSectionProps {
  filters: SourceFilter[];
  notificationEmails: string[];
  onFiltersChange: (filters: SourceFilter[]) => void;
  onNotificationEmailsChange: (emails: string[]) => void;
  showFiltersOnly?: boolean;
  showNotificationsOnly?: boolean;
}

const SourceFiltersSection: React.FC<SourceFiltersSectionProps> = ({
  filters,
  notificationEmails,
  onFiltersChange,
  onNotificationEmailsChange,
  showFiltersOnly = false,
  showNotificationsOnly = false,
}) => {
  const addFilter = () => {
    const newFilter: SourceFilter = {
      department: '',
      minPrice: 0,
    };
    onFiltersChange([...filters, newFilter]);
  };

  const updateFilter = (index: number, field: keyof SourceFilter, value: string | number) => {
    const updatedFilters = [...filters];
    updatedFilters[index] = {
      ...updatedFilters[index],
      [field]: value,
    };
    onFiltersChange(updatedFilters);
  };

  const removeFilter = (index: number) => {
    const updatedFilters = filters.filter((_, i) => i !== index);
    onFiltersChange(updatedFilters);
  };

  const addNotificationEmail = () => {
    onNotificationEmailsChange([...notificationEmails, '']);
  };

  const updateNotificationEmail = (index: number, value: string) => {
    const updatedEmails = [...notificationEmails];
    updatedEmails[index] = value;
    onNotificationEmailsChange(updatedEmails);
  };

  const removeNotificationEmail = (index: number) => {
    const updatedEmails = notificationEmails.filter((_, i) => i !== index);
    onNotificationEmailsChange(updatedEmails);
  };

  const departmentOptions: SelectOption[] = [
    { value: '', label: 'Select Department' },
    ...Object.entries(departmentValues).map(([key, label]) => ({
      value: key,
      label: String(label),
    })),
  ];

  return (
    <div>
      {!showNotificationsOnly && (
        <Card className={showFiltersOnly ? '' : 'mb-4'}>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-medium text-gray-900">Property Filters</h3>
                <p className="text-sm text-gray-600">
                  Only properties matching these criteria will be processed and stored
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={addFilter}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Filter
              </Button>
            </div>

            {filters.length === 0 ? (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 text-center">
                  No filters configured. All properties will be processed regardless of department or price.
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {filters.map((filter, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 p-4 border border-gray-300 rounded-lg bg-white"
                    >
                      <div className="flex-1 min-w-[200px]">
                        <Select
                          value={filter.department}
                          label="Department"
                          options={departmentOptions}
                          onChange={(e) => updateFilter(index, 'department', e.target.value)}
                          fullWidth
                        />
                      </div>
                      <div className="w-36">
                        <Input
                          label="Min Price (£)"
                          type="number"
                          value={filter.minPrice}
                          onChange={(e) => updateFilter(index, 'minPrice', parseInt((e.target as HTMLInputElement).value) || 0)}
                          fullWidth
                        />
                      </div>
                      <button
                        onClick={() => removeFilter(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Remove filter"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    💡 Properties must match at least one filter to be processed.
                    If department is selected, only properties from that department will be considered.
                    If min price is set, only properties above that price will be processed.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {!showFiltersOnly && (
        <Card>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-medium text-gray-900">Notification Emails</h3>
                <p className="text-sm text-gray-600">
                  Email addresses to notify when new properties are found
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={addNotificationEmail}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Email
              </Button>
            </div>

            {notificationEmails.length === 0 ? (
              <p className="text-sm text-gray-600">
                No notification emails configured. Property updates won't be sent.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {notificationEmails.map((email, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      label="Email Address"
                      type="email"
                      value={email}
                      onChange={(e) => updateNotificationEmail(index, (e.target as HTMLInputElement).value)}
                      fullWidth
                    />
                    <button
                      onClick={() => removeNotificationEmail(index)}
                      className="mt-6 p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Remove email"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SourceFiltersSection;
